/* ============================================================================
 * 索通 C+APS V3 · 主数据（真实企业档案取证）
 *
 * 来源：V2 sunstone-caps/js/data.js —— 该文件是经取证的真实主数据资产，
 *       V3 原样继承并加类型约束，不重新录入（版本演进纪律：真资产只增不减）。
 *       取证口径见各常量注释。阶段 3 起本文件迁入 PostgreSQL，前端改为 API 读取，
 *       此处保留为离线兜底与类型真源。
 * ========================================================================== */

/* ── 类型 ───────────────────────────────────────────────────────────── */

export type PlateCode = 'ANODE' | 'CALCINER' | 'NEG' | 'CAP';
export type ChainCode = 'FULL' | 'FULL_CPC' | 'SEMI' | 'CPC_ONLY' | 'NEG' | 'CAP';
export type PlantStatus = 'RUN' | 'RAMP' | 'PLANNED';
export type TierCode = 'FORT' | 'HOLD' | 'ATTACK';
export type RoleCode = 'PRESIDENT' | 'OPS' | 'PLANNER' | 'ADMIN';

export interface ProductionLine { code: string; name: string; cap: number; utilYtd?: number }

export interface Factory {
  code: string; name: string; short: string; legal: string;
  region: string; province: string;
  plate: PlateCode; chain: ChainCode; status: PlantStatus;
  /** 设计产能，阳极/煅后焦/负极为万吨/年；电容为亿支/年（见 capUnit） */
  capacity: number; capUnit?: string;
  people: number;
  /** 2026 年 1–5 月实测达成率（产能利用率台账）；未动工基地锁空 */
  utilYtd: number | null;
  quality: number | null;
  tonMargin: number | null;
  lng: number; lat: number;
  lines: ProductionLine[];
  note: string;
}

export interface Process {
  code: string; name: string;
  /** 时产能 吨/小时 */
  capPerHour: number;
  /** 换产准备工时 */
  setupH: number;
  wcPrefix: string;
  bottleneck?: boolean;
}

export interface Product {
  code: string; name: string; grade: string; sulfur: string; family: string;
  spec: string; pricePerTon: number; cokeRatio: number; desulfur: boolean; note: string;
}

export interface BomLine { mat: string; name: string; ratio: number }

export interface Material {
  code: string; name: string; unit: string; price: number;
  /** 安全库存天数 */
  safeDays: number;
  note: string;
}

export interface Customer {
  code: string; name: string; tier: TierCode;
  /** 年需求 万吨 */
  demand: number;
  /** 索通份额 % */
  share: number;
  factories: string[]; settle: string; region: string;
  export?: boolean; jv?: boolean;
  /** 竞品份额 %（攻坚客户）*/
  rival?: number;
}

export interface Direction { customer: string; product: string; factory: string; reason: string }

export interface Role {
  code: RoleCode; name: string; person: string; desc: string;
  nav: string[];
  can: Record<string, boolean>;
}

/* ── 元信息 ─────────────────────────────────────────────────────────── */

export const META = {
  appName: 'C+APS V3',
  appNameCn: '索通智能计划与排产平台',
  version: 'v3.0.0',
  planMonth: '2026-08',
  nextMonth: '2026-09',
  today: '2026-08-07',
  planOrigin: '2026-08-01',
  /** 硬闸门槛 */
  gates: {
    consistencyPct: 99.99,
    kittingPct: 80,
    solverFactorySec: 15,
    solverIncrementalSec: 5,
  },
} as const;

/* ── 行情基准（公开市场口径核实）───────────────────────────────────── */

export const MARKET = {
  anodeBenchmark: {
    source: '山东大型铝厂月度采购基准价（魏桥模式）',
    cash: 5674, draft: 5695, month: '2026-08', prevCash: 5548,
  },
  /** 月度招标价（现汇，元/吨）2–8 月 */
  anodeSeries: [
    { m: '2月', v: 5290 }, { m: '3月', v: 5390 }, { m: '4月', v: 5474 }, { m: '5月', v: 5672 },
    { m: '6月', v: 5548 }, { m: '7月', v: 5602 }, { m: '8月', v: 5674 },
  ],
} as const;

/* ── 大区 ───────────────────────────────────────────────────────────── */

export const REGIONS = [
  { code: 'HD', name: '华东大区', provinces: ['山东'] },
  { code: 'XN', name: '西南大区', provinces: ['云南', '重庆', '广西'] },
  { code: 'XB', name: '西北大区', provinces: ['甘肃', '内蒙古'] },
  { code: 'HZ', name: '华中华南', provinces: ['湖北', '广东'] },
  { code: 'HW', name: '海外', provinces: ['阿联酋'] },
] as const;

/* ── 生产基地（法人/链条/设计产能/定员 取证于 2026 绩效指标汇总表）──── */

export const FACTORIES: Factory[] = [
  { code: 'LY', name: '山东生产中心（临邑）', short: '临邑', legal: '索通发展股份有限公司', region: 'HD', province: '山东', plate: 'ANODE', chain: 'FULL', status: 'RUN', capacity: 28.8, people: 547, utilYtd: 105.0, quality: 95.2, tonMargin: 487, lng: 116.770, lat: 37.110, lines: [{ code: 'LY-1', name: '临邑一线', cap: 28.8 }], note: '集团发源地 · 国家卓越级智能工厂主体' },
  { code: 'QL', name: '索通齐力（临邑）', short: '齐力', legal: '索通齐力炭材料', region: 'HD', province: '山东', plate: 'ANODE', chain: 'FULL', status: 'RUN', capacity: 30, people: 319, utilYtd: 90.9, quality: 96.1, tonMargin: 508, lng: 116.950, lat: 37.270, lines: [{ code: 'QL-1', name: '齐力一线', cap: 30 }], note: '齐力铝业合资 · 利润完成率集团第一' },
  { code: 'CX', name: '山东索通创新（滨州）', short: '创新', legal: '山东索通创新炭材料', region: 'HD', province: '山东', plate: 'ANODE', chain: 'FULL_CPC', status: 'RUN', capacity: 94, people: 973, utilYtd: 99.7, quality: 94.4, tonMargin: 637, lng: 117.960, lat: 38.080, lines: [{ code: 'CX-1', name: '创新一期', cap: 60, utilYtd: 88.2 }, { code: 'CX-2', name: '创新二期', cap: 34, utilYtd: 120.3 }], note: '集团最大阳极基地 · 全链+煅后焦' },
  { code: 'JYGY', name: '嘉峪关预焙阳极', short: '嘉阳', legal: '嘉峪关索通预焙阳极', region: 'XB', province: '甘肃', plate: 'ANODE', chain: 'FULL', status: 'RUN', capacity: 30, people: 351, utilYtd: 101.2, quality: 97.5, tonMargin: 866, lng: 98.200, lat: 39.700, lines: [{ code: 'JYGY-1', name: '嘉阳一线', cap: 30 }], note: '东兴铝业配套 · 利润压舱石' },
  { code: 'JYGC', name: '嘉峪关炭材料', short: '嘉炭', legal: '嘉峪关索通炭材料', region: 'XB', province: '甘肃', plate: 'ANODE', chain: 'FULL', status: 'RUN', capacity: 34, people: 365, utilYtd: 80.8, quality: 97.2, tonMargin: 1004, lng: 98.380, lat: 39.840, lines: [{ code: 'JYGC-1', name: '嘉炭一线', cap: 34 }], note: '吨毛利 1004 元 · 集团利润总冠军' },
  { code: 'YL', name: '云南索通云铝（曲靖）', short: '云铝', legal: '云南索通云铝炭材料', region: 'XN', province: '云南', plate: 'ANODE', chain: 'FULL', status: 'RUN', capacity: 90, people: 898, utilYtd: 71.1, quality: 93.2, tonMargin: 372, lng: 103.800, lat: 25.490, lines: [{ code: 'YL-1', name: '云铝一期', cap: 60, utilYtd: 58.1 }, { code: 'YL-2', name: '云铝二期', cap: 30, utilYtd: 97.0 }], note: '云铝股份合资 · 一期修炉爬坡 · 唯一一级品率未达标基地' },
  { code: 'CQ', name: '重庆锦旗（綦江）', short: '重庆', legal: '重庆锦旗碳素', region: 'XN', province: '重庆', plate: 'ANODE', chain: 'FULL', status: 'RUN', capacity: 16, people: 255, utilYtd: 86.0, quality: 98.0, tonMargin: 641, lng: 106.650, lat: 29.030, lines: [{ code: 'CQ-1', name: '锦旗一线', cap: 16 }], note: '全集团质量最优基地（102.8%）' },
  { code: 'LX', name: '陇西索通', short: '陇西', legal: '陇西索通炭材料', region: 'XB', province: '甘肃', plate: 'ANODE', chain: 'SEMI', status: 'RUN', capacity: 30, people: 247, utilYtd: 103.1, quality: 99.0, tonMargin: 318, lng: 104.630, lat: 35.000, lines: [{ code: 'LX-1', name: '陇西一线', cap: 30 }], note: '非全链（无煅烧车间）· 外购煅后焦套利敏感' },
  { code: 'ZJ', name: '湖北索通（枝江）', short: '枝江', legal: '湖北索通炭材料', region: 'HZ', province: '湖北', plate: 'CALCINER', chain: 'CPC_ONLY', status: 'RUN', capacity: 100, people: 250, utilYtd: 103.2, quality: 96.8, tonMargin: 201, lng: 111.760, lat: 30.420, lines: [{ code: 'ZJ-1', name: '枝江煅烧线', cap: 100 }], note: '集团煅后焦中央厨房 · 内供+外销' },
  { code: 'GX', name: '广西田东百矿（百色）', short: '田东', legal: '田东百矿三田碳素', region: 'XN', province: '广西', plate: 'ANODE', chain: 'FULL', status: 'RAMP', capacity: 60, people: 621, utilYtd: 53.7, quality: 93.5, tonMargin: 285, lng: 106.620, lat: 23.900, lines: [{ code: 'GX-1', name: '三田一线', cap: 60 }], note: '吉利百矿合资 66/34 · 项目期爬坡（烘炉转产中）' },
  { code: 'SY', name: '甘肃索通盛源（嘉峪关）', short: '盛源', legal: '甘肃索通盛源碳材料', region: 'XB', province: '甘肃', plate: 'NEG', chain: 'NEG', status: 'RUN', capacity: 2.5, people: 291, utilYtd: 92.0, quality: 96.0, tonMargin: -120, lng: 98.140, lat: 39.830, lines: [{ code: 'SY-1', name: '盛源石墨化线', cap: 2.5 }], note: '锂电负极 · 绿电富集区选址' },
  { code: 'XY', name: '内蒙古欣源（乌兰察布）', short: '欣源', legal: '内蒙古欣源石墨烯科技', region: 'XB', province: '内蒙古', plate: 'NEG', chain: 'NEG', status: 'RUN', capacity: 5.5, people: 445, utilYtd: 87.3, quality: 95.5, tonMargin: -85, lng: 113.130, lat: 41.030, lines: [{ code: 'XY-1', name: '欣源一线', cap: 5.5 }], note: '锂电负极 · 前驱体+石墨化' },
  { code: 'FS', name: '佛山欣源电子', short: '佛山', legal: '佛山市欣源电子（839229）', region: 'HZ', province: '广东', plate: 'CAP', chain: 'CAP', status: 'RUN', capacity: 20, capUnit: '亿支', people: 355, utilYtd: 75.0, quality: 97.0, tonMargin: 0, lng: 113.120, lat: 23.020, lines: [{ code: 'FS-1', name: '电容产线群', cap: 20 }], note: '薄膜电容 · 新三板挂牌 · 索通持股 94.98%' },
  { code: 'EGA', name: 'EGA 阿布扎比（未动工）', short: 'EGA', legal: '索通-EGA 合资（55%）', region: 'HW', province: '阿联酋', plate: 'ANODE', chain: 'SEMI', status: 'PLANNED', capacity: 30, people: 0, utilYtd: null, quality: null, tonMargin: null, lng: 54.370, lat: 24.450, lines: [], note: '已签正式合资协议（2025-12-16）· 一期 30 万吨 · 未动工 · 运营字段锁空' },
];

export const PLATE_NAME: Record<PlateCode, string> = { ANODE: '预焙阳极', CALCINER: '煅后焦', NEG: '锂电负极', CAP: '薄膜电容' };
export const CHAIN_NAME: Record<ChainCode, string> = { FULL: '全链', FULL_CPC: '全链+煅后焦', SEMI: '非全链', CPC_ONLY: '仅煅烧', NEG: '负极', CAP: '电容' };
export const STATUS_NAME: Record<PlantStatus, string> = { RUN: '在产', RAMP: '爬坡', PLANNED: '未动工' };
export const TIER_NAME: Record<TierCode, string> = { FORT: '堡垒', HOLD: '防守', ATTACK: '攻坚' };

/* ── 工序（七步工艺，焙烧为全厂瓶颈）─────────────────────────────────── */

export const PROCESSES: Process[] = [
  { code: 'CALCINING', name: '煅烧', capPerHour: 30, setupH: 2, wcPrefix: '煅烧炉' },
  { code: 'FORMING', name: '成型', capPerHour: 28, setupH: 1.5, wcPrefix: '成型机' },
  { code: 'BAKING', name: '焙烧', capPerHour: 22, setupH: 5, wcPrefix: '焙烧炉', bottleneck: true },
  { code: 'CLEANING', name: '清理', capPerHour: 40, setupH: 0.5, wcPrefix: '清理线' },
  { code: 'SLOTTING', name: '开槽', capPerHour: 36, setupH: 1, wcPrefix: '开槽机' },
  { code: 'PACKING', name: '打包', capPerHour: 45, setupH: 0.5, wcPrefix: '打包线' },
];

export const CHAIN_PROCESSES: Partial<Record<ChainCode, string[]>> = {
  FULL: ['CALCINING', 'FORMING', 'BAKING', 'CLEANING', 'SLOTTING', 'PACKING'],
  FULL_CPC: ['CALCINING', 'FORMING', 'BAKING', 'CLEANING', 'SLOTTING', 'PACKING'],
  SEMI: ['FORMING', 'BAKING', 'CLEANING', 'SLOTTING', 'PACKING'],
  CPC_ONLY: ['CALCINING'],
};

/* ── 产品（硫分梯级 + 魏桥基准价挂钩）───────────────────────────────── */

export const PRODUCTS: Product[] = [
  { code: 'P1', name: 'P1 低硫阳极', grade: 'P1', sulfur: '≤1.5%', family: '预焙阳极', spec: '1450#/1320#', pricePerTon: 5754, cokeRatio: 0.70, desulfur: false, note: '低硫溢价 +80（对基准价）' },
  { code: 'P2', name: 'P2 中硫阳极', grade: 'P2', sulfur: '1.5–2.5%', family: '预焙阳极', spec: '1450#/1550#', pricePerTon: 5674, cokeRatio: 0.70, desulfur: false, note: '执行现汇基准价' },
  { code: 'P3', name: 'P3 高硫阳极', grade: 'P3', sulfur: '≥2.5%', family: '预焙阳极', spec: '1450#', pricePerTon: 5554, cokeRatio: 0.78, desulfur: true, note: '高硫贴水 -120 · 需脱硫' },
  { code: 'CPC', name: '煅后焦', grade: 'CPC', sulfur: '分级', family: '煅后焦', spec: '低/中/高硫', pricePerTon: 4080, cokeRatio: 1.0, desulfur: false, note: '半成品 · 内供+外销' },
  { code: 'NEG', name: '人造石墨负极', grade: 'NEG', sulfur: '—', family: '锂电负极', spec: '动力/储能', pricePerTon: 26800, cokeRatio: 0, desulfur: false, note: '独立业态 · 电池厂认证制' },
];

/* ── BOM 配方（行业基准 65/20/15，硫分维度动态配比）──────────────────── */

export const BOM: Record<string, BomLine[]> = {
  P1: [{ mat: 'COKE_L', name: '石油焦（低硫）', ratio: 0.70 }, { mat: 'BUTT', name: '残极', ratio: 0.20 }, { mat: 'PITCH', name: '煤沥青', ratio: 0.155 }],
  P2: [{ mat: 'COKE_M', name: '石油焦（中硫）', ratio: 0.70 }, { mat: 'BUTT', name: '残极', ratio: 0.20 }, { mat: 'PITCH', name: '煤沥青', ratio: 0.155 }],
  P3: [{ mat: 'COKE_H', name: '石油焦（高硫）', ratio: 0.78 }, { mat: 'BUTT', name: '残极', ratio: 0.17 }, { mat: 'PITCH', name: '煤沥青', ratio: 0.16 }],
  CPC: [{ mat: 'COKE_RAW', name: '石油焦（原料焦）', ratio: 1.25 }],
};

export const MATERIALS: Material[] = [
  { code: 'COKE_L', name: '石油焦（低硫）', unit: '吨', price: 3760, safeDays: 10, note: '结构性紧缺 · "从贵不贵到有没有"' },
  { code: 'COKE_M', name: '石油焦（中硫）', unit: '吨', price: 3310, safeDays: 8, note: '主力焦种 · 占比 64.3%' },
  { code: 'COKE_H', name: '石油焦（高硫）', unit: '吨', price: 2980, safeDays: 8, note: '技术置换主力' },
  { code: 'COKE_RAW', name: '石油焦（原料焦）', unit: '吨', price: 3180, safeDays: 12, note: '枝江煅烧投料' },
  { code: 'PITCH', name: '煤沥青', unit: '吨', price: 4460, safeDays: 5, note: '深加工开工率 47.8% · 供给偏紧' },
  { code: 'BUTT', name: '残极', unit: '吨', price: 2350, safeDays: 14, note: '客户返回料 · 循环利用' },
  { code: 'CPC_EXT', name: '煅后焦（外购）', unit: '吨', price: 4150, safeDays: 7, note: '陇西非全链依赖 · 倒挂风险监控' },
];

/* ── 客户档位（13 核心客户 570.79 万吨 · 堡垒/防守/攻坚三层策略）────── */

export const CUSTOMERS: Customer[] = [
  { code: 'WQ', name: '山东魏桥', tier: 'FORT', demand: 132.5, share: 92, factories: ['CX', 'LY', 'QL'], settle: '月度基准价·一级品率结算', region: '山东' },
  { code: 'YLGF', name: '云铝股份', tier: 'FORT', demand: 88.0, share: 95, factories: ['YL'], settle: '公式计价（招标价-64）', region: '云南', jv: true },
  { code: 'DX', name: '酒钢东兴铝业', tier: 'FORT', demand: 62.0, share: 96, factories: ['JYGY', 'JYGC'], settle: '长协·公式计价', region: '甘肃' },
  { code: 'QLLY', name: '齐力铝业', tier: 'FORT', demand: 30.0, share: 97, factories: ['QL'], settle: '合资长协', region: '山东', jv: true },
  { code: 'BK', name: '广西百矿集团', tier: 'HOLD', demand: 52.0, share: 74, factories: ['GX'], settle: '协议价（魏桥-80）', region: '广西', jv: true },
  { code: 'RT', name: '力拓 Rio Tinto', tier: 'HOLD', demand: 28.0, share: 61, factories: ['CX', 'LY'], settle: '出口长协·船期结算', region: '出口', export: true },
  { code: 'HY', name: '海德鲁 Hydro', tier: 'HOLD', demand: 21.0, share: 55, factories: ['CX'], settle: '出口长协', region: '出口', export: true },
  { code: 'EGAC', name: 'EGA（客户侧）', tier: 'HOLD', demand: 24.0, share: 48, factories: ['CX', 'LY'], settle: '出口长协', region: '出口', export: true, jv: true },
  { code: 'HF', name: '华峰集团', tier: 'HOLD', demand: 18.0, share: 52, factories: ['LY', 'QL'], settle: '现货+长协', region: '华东' },
  { code: 'SZHQ', name: '深圳宏桥', tier: 'ATTACK', demand: 199.02, share: 18.5, factories: ['CX'], settle: '现货试单', region: '华南', rival: 81.5 },
  { code: 'YNHT', name: '云南宏泰', tier: 'ATTACK', demand: 94.39, share: 43.9, factories: ['YL', 'CQ'], settle: '现货+意向长协', region: '云南', rival: 56.1 },
  { code: 'NMCY', name: '内蒙创源', tier: 'ATTACK', demand: 36.27, share: 3.7, factories: ['XY'], settle: '试单', region: '内蒙古', rival: 96.3 },
  { code: 'QHXB', name: '青海西部水电', tier: 'ATTACK', demand: 21.82, share: 13.9, factories: ['JYGY'], settle: '试单', region: '青海', rival: 86.1 },
];

/* ── 产品定向（第一优先级硬约束：客户+产品 锁定工厂）────────────────── */

export const DIRECTIONS: Direction[] = [
  { customer: 'YLGF', product: 'P2', factory: 'YL', reason: '云铝合资协议锁定就近供应' },
  { customer: 'DX', product: 'P1', factory: 'JYGY', reason: '东兴长协·低硫专供' },
  { customer: 'DX', product: 'P2', factory: 'JYGC', reason: '东兴长协·嘉炭产线绑定' },
  { customer: 'QLLY', product: 'P2', factory: 'QL', reason: '齐力合资协议锁定' },
  { customer: 'BK', product: 'P3', factory: 'GX', reason: '百矿合资·田东就近' },
  { customer: 'WQ', product: 'P2', factory: 'CX', reason: '魏桥 1450# 主供创新' },
];

/* ── 四级优先级规则（可配置，默认值）────────────────────────────────── */

export const RULES_DEFAULT = {
  l1: { name: '产品定向', enabled: true, hard: true, desc: '客户+产品锁定工厂（合资协议/长协绑定），硬约束不可违反' },
  l2: { name: '工艺兼容', enabled: true, hard: true, desc: 'P3 高硫仅可排入具备脱硫能力工厂；非全链厂不可排煅烧工序' },
  l3: { name: '利润最优', enabled: true, hard: false, weight: 0.6, desc: 'Maximize Σ(售价−原料−制造−运输)×量' },
  l4: { name: '交期满足', enabled: true, hard: false, weight: 0.4, latePenalty: 10, splitPenalty: 4, desc: '欠产惩罚+拆单惩罚，紧急单权重 ×5' },
};

/* ── 成本模型（锚定真实吨毛利台账反推）──────────────────────────────── */

export interface CostCell { raw: number; mfg: number; transport: number; margin: number }

const MFG_BASE: Record<string, number> = { LY: 720, QL: 705, CX: 690, JYGY: 655, JYGC: 640, YL: 760, CQ: 700, LX: 730, GX: 810 };
const TRANSPORT: Record<string, number> = { LY: 95, QL: 95, CX: 88, JYGY: 132, JYGC: 132, YL: 76, CQ: 92, LX: 118, GX: 84 };

export const COST: Record<string, Record<string, CostCell>> = (() => {
  const out: Record<string, Record<string, CostCell>> = {};
  const matPrice = (code: string) => MATERIALS.find((m) => m.code === code)?.price ?? 0;
  for (const f of FACTORIES) {
    if (f.plate !== 'ANODE' || f.status === 'PLANNED') continue;
    out[f.code] = {};
    for (const p of ['P1', 'P2', 'P3']) {
      const prod = PRODUCTS.find((x) => x.code === p)!;
      let raw = 0;
      for (const b of BOM[p]) raw += b.ratio * matPrice(b.mat);
      // 非全链厂需外购煅后焦，按 0.32 系数计溢价（对 3980 基准）
      if (f.chain === 'SEMI') raw += 0.32 * (matPrice('CPC_EXT') - 3980);
      const mfg = (MFG_BASE[f.code] ?? 720) + (p === 'P3' ? 55 : 0);
      const transport = TRANSPORT[f.code] ?? 100;
      out[f.code][p] = {
        raw: Math.round(raw), mfg, transport,
        margin: Math.round(prod.pricePerTon - raw - mfg - transport),
      };
    }
  }
  return out;
})();

/* ── 名词解读词表（F-71：V2 全站仅 24 处术语解读，术语密度却极高）───── */

export const GLOSSARY: Record<string, { name: string; text: string }> = {
  'CP-SAT': { name: 'CP-SAT 约束求解', text: 'Google OR-Tools 的约束规划求解器。把排产写成数学约束（产能、交期、工序串行、设备互斥），在可行域内寻找可证明最优解，替代"老师傅经验排产"。' },
  '双层排产': { name: '双层排产', text: '第一层"运营级派单"把订单分配到工厂（集团视角），第二层"工厂级工序排产"在厂内 6 道工序上细排（车间视角）。两层结果必须结构一致。' },
  '结构一致性': { name: '双层结构一致性', text: '计划订单被工厂级排程完整纳入的比例。审批校验硬卡 ≥99.99%，未达标不允许下发——保证"集团派的"与"工厂排的"是同一件事。' },
  '齐套率': { name: '物料齐套率', text: '按 BOM 展开净需求后，可用物料（在库+在途）对需求的覆盖比例。低于 80% 门槛时审批被硬拦截，缺料先走采购联动。' },
  '焙烧瓶颈': { name: '焙烧瓶颈', text: '焙烧炉时产能 22 吨/时、换产需 5 小时，且火焰周期长，是全厂产出的天花板。排产以焙烧为鼓点（DBR），其他工序围绕它对齐。' },
  'M+0': { name: 'M+0 / M+1 滚动', text: '月度滚动规划把当月（M+0）锁定为执行基线，下月（M+1）预排不锁。每月滚动一次，兼顾稳定执行与柔性调整。' },
  '波动响应': { name: '波动响应分级', text: '原料/设备/质量/客户/政策五类波动源，按影响自动评为 L1（记录即可）/L2（需运营签注，触发 ≤5 秒增量求解）/L3（升级集团研判）。' },
  '增量求解': { name: '增量求解', text: '波动发生后不推倒重排：锁定在产与 3 天内工序，只在受影响边界内重新求解，≤5 秒出结果，改动最小化。' },
  '齐套门': { name: '齐套门（80%）', text: '排产审批校验步的第二道硬闸：最近一次派单的物料齐套率必须 ≥80%，否则 422 拦截，不允许下发。' },
  '一级品率': { name: '一级品率', text: '客户验收口径的最高等级品比例，直接挂钩质量奖励（2025 年 7337 万）与结算价。各基地目标按客户要求差异化设定。' },
  '销产比': { name: '销产比', text: '销量 ÷ 产量。低于 98% 触发经营熔断预警——产量达标而销售滞后会形成库存占压（当前头号经营矛盾）。' },
  '升贴水': { name: '升贴水', text: '结算价 = 魏桥月度基准价 ± 升贴水。升贴水由品质（硫分梯级）、区域运费、客户协议共同决定。' },
  '产品定向': { name: '产品定向（第一优先级）', text: '合资协议或长协把"客户+产品"硬锁定到指定工厂（如云铝订单锁云铝厂），派单求解时作为硬约束优先满足。' },
  '冻结窗口': { name: '冻结窗口', text: '紧急插单时，已开工与 72 小时内即将开工的工序被冻结不动，只对窗口之外的排程做增量调整，保护现场执行稳定。' },
  '决策日志': { name: '决策日志', text: '每次求解输出结构化中文决策依据（为什么这张单派这个厂），业务可读、可追溯、可审计。' },
  '行动信封': { name: '行动信封', text: 'AI 副驾输出的结构化动作：回复/卡片/导航/操作建议。所有不可逆操作只生成"候选"，必须由人在界面上批准执行。' },
  '8因子归因': { name: '8 因子利润归因', text: '把利润缺口拆解为销量差/收入差/成本差/汇率差/客户结构差/质量差/产量差/回款差八个因子，定位到法人与客户级。' },
  'OEM': { name: 'OEM 代焙/外采', text: '自产产能不足时外采补量或委托代焙。外采必须守成本红线——外购价高于售价即单吨亏损（田东案例：-173.95 元/吨）。' },
  '任务谱系': { name: 'task_trace 任务谱系', text: '每个 AI 研判、每次求解从触发到落地的完整链路记录（谁触发→哪个模型→什么证据→谁签注），双面输出一致性可审查。' },
  'makespan': { name: 'makespan 制造周期', text: '从第一道工序开工到最后一道工序完工的总跨度。排产目标函数的第一项，压缩它意味着更快清空订单池。' },
  '拖期': { name: '加权拖期', text: '订单完工时间超出交期的天数，按订单优先级加权求和（紧急单权重 ×5）。目标函数第二项。' },
  '改动偏离': { name: '改动偏离', text: '重排后的工序开工时间与上一版排程的偏差绝对值之和。目标函数第三项，让增量求解"改动最小化"而不是推倒重来。' },
};

/* ── 角色与权限（RBAC）─────────────────────────────────────────────── */

/** 权限键 → 业务语言。界面上不许出现 `runSolver` 这种键名。
 *  与服务端 `auth.py::CAN_NAME` 同源同义；服务端 `/api/auth/meta` 也会返回一份，
 *  这里保留一份用于离线态与按钮禁用提示。 */
export const CAN_NAME: Record<string, string> = {
  runSolver: '运行求解', approve: '审批', sign: '签注', endorse: '背书', submit: '提交审批',
  approveBaseline: '审议月度基线', editOrder: '编辑订单',
  manageMaster: '维护主数据', manageUser: '管理用户', admin: '系统管理', viewAll: '全局查看',
};

export const ROLES: Record<RoleCode, Role> = {
  PRESIDENT: {
    code: 'PRESIDENT', name: '总裁 · 经营会', person: '郎静',
    desc: '决策层：全局透视 + 月度基线审议快通道，只读为主',
    nav: ['home', 'overview', 'plant', 'reports', 'benchmark', 'demand', 'sop', 'plan', 'orders', 'atp', 'customer', 'tower', 'trace'],
    can: { approveBaseline: true, viewAll: true },
  },
  OPS: {
    code: 'OPS', name: '运营管理中心', person: '石钰',
    desc: '计划决策：派单拍板、排产审批、异常签注、S&OP 规划',
    nav: ['home', 'overview', 'plant', 'reports', 'benchmark', 'demand', 'sop', 'plan', 'operational', 'factory', 'approval', 'emergency', 'whatif', 'capacity', 'rules', 'orders', 'atp', 'customer', 'material', 'inventory', 'resources', 'tower', 'trace'],
    can: { runSolver: true, approve: true, sign: true, submit: true, endorse: true, approveBaseline: true, editOrder: true },
  },
  PLANNER: {
    code: 'PLANNER', name: '计划员 / 排产工程师', person: '排产组',
    desc: '操作层：车间排程、紧急插单、情景模拟、物料齐套',
    nav: ['home', 'plant', 'demand', 'plan', 'operational', 'factory', 'approval', 'emergency', 'whatif', 'capacity', 'orders', 'atp', 'customer', 'material', 'inventory', 'resources', 'trace'],
    can: { runSolver: true, submit: true, sign: true, editOrder: true },
  },
  ADMIN: {
    code: 'ADMIN', name: '系统管理员', person: '数据智能中心',
    desc: '支撑层：主数据、集成中台、权限、AI 配置',
    nav: ['home', 'overview', 'plant', 'reports', 'benchmark', 'demand', 'sop', 'plan', 'operational', 'factory', 'approval', 'emergency', 'whatif', 'capacity', 'rules', 'orders', 'atp', 'customer', 'material', 'inventory', 'resources', 'tower', 'trace', 'master', 'integration', 'permissions', 'aiconfig'],
    can: { admin: true, viewAll: true },
  },
};

/* ── 查询助手 ───────────────────────────────────────────────────────── */

export const factoryByCode = (code: string) => FACTORIES.find((f) => f.code === code);
export const factoryShort = (code: string) => factoryByCode(code)?.short ?? code;
export const productByCode = (code: string) => PRODUCTS.find((p) => p.code === code);
export const customerByCode = (code: string) => CUSTOMERS.find((c) => c.code === code);
export const materialByCode = (code: string) => MATERIALS.find((m) => m.code === code);
export const processByCode = (code: string) => PROCESSES.find((p) => p.code === code);

/** 在产阳极基地（派单求解的候选集） */
export const ANODE_FACTORIES = FACTORIES.filter((f) => f.plate === 'ANODE' && f.status !== 'PLANNED');
/** 具备脱硫能力的工厂（P3 高硫的硬约束候选集）*/
export const DESULFUR_FACTORIES = ['CX', 'JYGY', 'JYGC', 'YL', 'GX'];
